import React, {
  useState,
  useCallback,
  useEffect,
  useMemo
} from 'react'

import PropTypes from 'prop-types'

import filter from 'lodash/filter'
import find from 'lodash/find'
import get from 'lodash/get'
import indexOf from 'lodash/indexOf'
import isEmpty from 'lodash/isEmpty'
import isFunction from 'lodash/isFunction'
import isNil from 'lodash/isNil'
import map from 'lodash/map'
import uniqBy from 'lodash/uniqBy'
import upperFirst from 'lodash/upperFirst'

import { Typography } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'

import { useT } from '@galo-monitor/i18n'
import TextField from '@galo-monitor/web-components/TextField'

import useStyles from './styles'

const InputSelectStyled = (props) => {
  const {
    options: externalOptions,
    initialOptions,
    styleOptions,
    customAutocompleteStyles,
    multiple,
    creatable,
    onCreateNewOption,
    insertOnEnter,
    size,
    defaultValue: externalDefaultValue,
    // eslint-disable-next-line react/prop-types
    zIndex,
    asyncOptionLabelField,
    asyncOptionValueField,
    urlParams,
    queryParams,
    disableClearable,
    disabled,
    value,
    onChange,
    onBlur,
    error,
    loading: externalLoading,
    ...otherProps
  } = props

  const classes = useStyles()
  const t = useT()

  const [open, setOpen] = useState(false)
  const [valueInput, setValueInput] = useState('')
  const [internalLoading, setLoading] = useState(false)
  const [asyncData, setAsyncData] = useState([])
  const [createdOptions, setCreatedOptions] = useState([])
  const [cachedData, setCachedData] = useState([])

  const loading = useMemo(
    () => externalLoading || internalLoading,
    [externalLoading, internalLoading]
  )

  const asyncOptions = useMemo(
    () => map(asyncData, option => ({
      value: String(get(option, asyncOptionValueField)),
      label: get(option, asyncOptionLabelField)
    })),
    [asyncData, asyncOptionLabelField, asyncOptionValueField]
  )

  const cachedOptions = useMemo(
    () => map(cachedData, option => ({
      value: String(get(option, asyncOptionValueField)),
      label: get(option, asyncOptionLabelField)
    })),
    [cachedData, asyncOptionLabelField, asyncOptionValueField]
  )

  const asyncMode = useMemo(
    () => isFunction(externalOptions),
    [externalOptions]
  )

  const options = useMemo(
    () => {
      let newOptions = [
        ...(asyncMode ? asyncOptions : externalOptions),
        ...cachedOptions,
        ...createdOptions,
        ...initialOptions
      ]
      if (creatable && !find(newOptions, { value }) && !isEmpty(value)) {
        newOptions = [
          { value, label: upperFirst(value) },
          ...newOptions
        ]
      }
      return uniqBy(newOptions, 'value')
    },
    [asyncMode, asyncOptions, cachedOptions, creatable, createdOptions, externalOptions, initialOptions, value]
  )

  // const {
  //   fieldName,
  //   fieldRef,
  //   formRef,
  //   handleChangeNative,
  //   handleBlurNative,
  //   setTouched,
  //   resetField,
  //   externalOnChange,
  //   validateField,
  //   error
  // } = useField()

  const optionSelected = useMemo(
    () => {
      const defaultReturn = multiple ? [] : null
      if (multiple) {
        return map(
          value,
          defVal => find(options, option => option.value === defVal)
        )
      }
      if (!isEmpty(value)) {
        return find(options, option => option.value === value) || defaultReturn
      }
      return defaultReturn
    },
    [multiple, options, value]
  )

  const handleChange = useCallback(
    (_, newValue, reason) => {
      let val
      let activeAsyncItem

      if (multiple) {
        val = map(newValue, item => item.value)
        activeAsyncItem = filter(asyncData, (item) => indexOf(val, get(item, asyncOptionValueField)) > -1)
      } else {
        val = get(newValue, 'value', '')
        activeAsyncItem = find(asyncData, (item) => get(item, asyncOptionValueField) === val)
      }

      setOpen(false)
      onChange(val, activeAsyncItem, reason)
    },
    [asyncData, asyncOptionValueField, multiple, onChange]
  )

  const handleCreatedOption = useCallback(
    (inputValue) => {
      const newOption = { value: inputValue, label: inputValue, created: true }
      const hasItem = find(createdOptions, option => option.label === newOption.label)

      if(!isNil(onCreateNewOption)) {
        onCreateNewOption(newOption)
      } else if (!hasItem) {
        setCreatedOptions([...createdOptions, newOption])
      }

      handleChange(null, newOption)
    },
    [createdOptions, handleChange, onCreateNewOption]
  )

  const handleKeyDown = useCallback(
    ({ keyCode, target: { value: inputValue } }) => {
      const ENTER_KEY = 13
      if (creatable && keyCode === ENTER_KEY && inputValue !== '') {
        handleCreatedOption(inputValue)
        handleChange(null, { label: valueInput, value: valueInput })
      }
    },
    [creatable, handleChange, handleCreatedOption, valueInput]
  )

  const handleInsertOnClick = useCallback(
    () => {
      if (creatable && valueInput !== '') {
        handleCreatedOption(valueInput)
        handleChange(null, { label: valueInput, value: valueInput })
        setOpen(false)
      }
    },
    [creatable, handleChange, handleCreatedOption, valueInput]
  )

  const noOptionsText = useMemo(() => {
    if (loading)
      return t('loading')

    if (creatable && !valueInput)
      return t('type to search {this}', {
        gender: 'female',
        howMany: 1,
        this: otherProps.label || ''
      })

    return creatable ? (
      <Typography
        className={ classes.insertOnClickOption }
        onMouseDown={ event => event.preventDefault() }
        onMouseUp={ handleInsertOnClick }
      >
        {insertOnEnter ? t('press enter to include') : t('add new entry')} {`"${ valueInput }"`}
      </Typography>
    ) : t('no options')
  }, [classes.insertOnClickOption, creatable, handleInsertOnClick, insertOnEnter, loading, otherProps.label, t, valueInput])

  const loadAsyncOptions = useCallback(
    async () => {
      setCachedData(prevState => [...prevState, ...asyncData])
      try {
        setLoading(true)
        const response = await externalOptions(queryParams, urlParams)

        setAsyncData(response?.data ?? [])
      } finally {
        setLoading(false)
      }
    },
    [asyncData, externalOptions, queryParams, urlParams]
  )

  useEffect(() => {
    if (asyncMode) {
      loadAsyncOptions()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asyncMode, queryParams, urlParams])

  return (
    <Autocomplete
      // freeSolo
      open={ open }
      disabled={ disabled }
      options={ options }
      classes={ {
        option: classes.option
      } }
      loading={ loading }
      value={ optionSelected }
      onChange={ handleChange }
      onFocus={ () => setOpen(true) }
      onBlur={ () => setOpen(false) }
      noOptionsText={ noOptionsText }
      multiple={ multiple }
      autoHighlight
      disableClearable={ disableClearable }
      getOptionLabel={ (option) => option?.label }
      renderOption={ (option) => <span { ...styleOptions }>{option?.label}</span> }
      style={ { ...customAutocompleteStyles } }
      renderInput={ (params) => (
        <TextField
          { ...otherProps }
          { ...params }
          detached
          error={ error }
          onBlur={ onBlur }
          onKeyDown={ handleKeyDown }
          onChange={ (e) => setValueInput(e.target.value) }
          onClick={ () => !disabled && setOpen(true) }
          size={ size }
          fullWidth
          InputProps={ {
            ...params.InputProps,
            ...(otherProps.InputProps || {}),
            autoComplete: 'new-password' // disable autocomplete and autofill
          } }
        />
      ) }
    />
  )
}

InputSelectStyled.propTypes = {
  options: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string
    })),
    PropTypes.func
  ]),
  initialOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string
  })),
  styleOptions: PropTypes.object,
  customAutocompleteStyles: PropTypes.object,
  creatable: PropTypes.bool,
  onCreateNewOption: PropTypes.func,
  insertOnEnter: PropTypes.bool,
  disabled: PropTypes.bool,
  multiple: PropTypes.bool,
  size: PropTypes.string,
  defaultValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string)
  ]),
  asyncOptionLabelField: PropTypes.string,
  asyncOptionValueField: PropTypes.string,
  urlParams: PropTypes.object,
  queryParams: PropTypes.object,
  disableClearable: PropTypes.bool,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string)
  ]).isRequired,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  error: PropTypes.string,
  loading: PropTypes.bool
}

InputSelectStyled.defaultProps = {
  options: [],
  initialOptions: [],
  customAutocompleteStyles: {},
  styleOptions: {},
  creatable: false,
  onCreateNewOption: null,
  insertOnEnter: false,
  disabled: false,
  multiple: false,
  defaultValue: undefined,
  asyncOptionLabelField: 'name',
  asyncOptionValueField: 'id',
  size: 'small',
  urlParams: {},
  queryParams: {},
  disableClearable: false,
  onChange: () => {},
  onBlur: () => {},
  error: null,
  loading: false
}

export default InputSelectStyled
